<template>
  <div class="report-page fee-type-collection-report">
    <ReportViewBar title="Collection by Fee Type" :start="start" :end="end" />

    <v-container fluid>
      <div class="report-page__intro no-print">
        <div>
          <div class="report-page__eyebrow">School revenue</div>
          <div class="headline font-weight-bold">Fee type collection</div>
          <div class="report-page__description">
            Compare invoices, families, and payments for each fee type and department.
          </div>
        </div>
        <div class="report-actions">
          <div class="report-period">
            <v-icon small color="primary">mdi-calendar-range</v-icon>
            <span>{{ formattedPeriod }}</span>
          </div>
          <v-btn color="primary" depressed :loading="isDownloadingPdf" :disabled="!rows.length" @click="downloadPdf">
            <v-icon left>mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>

      <v-alert v-if="error" type="error" outlined class="mb-5">
        {{ error }}
        <v-btn text small color="error" class="ml-2" @click="loadReport">Retry</v-btn>
      </v-alert>

      <template v-if="isLoading">
        <v-progress-linear indeterminate color="primary" class="mb-4" />
        <v-skeleton-loader type="card, table" />
      </template>

      <template v-else-if="!error">
        <v-row class="mb-2">
          <v-col cols="12" sm="6" md="4" class="report-kpi-col">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Amount invoiced</div>
                <div class="report-kpi__value">{{ totals.invoiced | currency }}</div>
                <div class="report-kpi__hint">Fee charges on invoices created in this period</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="report-kpi-col">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Amount collected</div>
                <div class="report-kpi__value primary--text">{{ totals.collected | currency }}</div>
                <div class="report-kpi__hint">Payments received in this period</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="report-kpi-col">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Merchant collected</div>
                <div class="report-kpi__value merchant-amount">{{ totals.merchantCollected | currency }}</div>
                <div class="report-kpi__hint">Net payments processed through merchant integrations</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="report-kpi-col">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Charges created</div>
                <div class="report-kpi__value">{{ totals.chargeCount }}</div>
                <div class="report-kpi__hint">Invoice detail rows created in this period</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="report-kpi-col">
            <v-card flat class="report-kpi">
              <v-card-text>
                <div class="report-kpi__label">Collection rate</div>
                <div class="report-kpi__value">{{ totals.collectionRate }}%</div>
                <div class="report-kpi__hint">Collected amount divided by invoiced amount</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card flat class="report-section mb-5">
          <div class="report-section__header">
            <div>
              <div class="report-section__title">Amount collected by fee type and department</div>
              <div class="report-section__subtitle">Money received during the selected period</div>
            </div>
            <v-chip small outlined color="primary">{{ rows.length }} fee types</v-chip>
          </div>
          <div v-show="rows.length" ref="chartContainer" class="fee-type-collection-report__chart" />
          <div v-if="!rows.length" class="report-empty">
            <v-icon large color="grey lighten-1">mdi-chart-box-outline</v-icon>
            <div class="mt-2 font-weight-medium">No school fee activity found</div>
            <div>Try another reporting period.</div>
          </div>
        </v-card>

        <v-card v-if="rows.length" flat class="report-section">
          <div class="report-section__header">
            <div>
              <div class="report-section__title">Fee type details</div>
              <div class="report-section__subtitle">
                Counts are per fee type and department; an invoice or parent with multiple combinations appears in each matching row.
              </div>
            </div>
          </div>
          <v-data-table
            :headers="headers"
            :items="rows"
            :items-per-page="rows.length"
            :sort-by="['departmentName', 'feeTypeName']"
            :sort-desc="[false, false]"
            hide-default-footer
            class="report-table"
          >
            <template v-slot:item.feeTypeName="{ item }">
              <span class="font-weight-medium">{{ item.feeTypeName }}</span>
            </template>
            <template v-slot:item.departmentName="{ item }">
              <span>{{ item.departmentName }}</span>
            </template>
            <template v-slot:item.invoicedAmount="{ item }">
              <strong class="report-amount">{{ item.invoicedAmount | currency }}</strong>
            </template>
            <template v-slot:item.collectedAmount="{ item }">
              <strong class="report-amount report-amount--primary">{{ item.collectedAmount | currency }}</strong>
            </template>
            <template v-slot:item.merchantCollectedAmount="{ item }">
              <strong class="report-amount">{{ item.merchantCollectedAmount | currency }}</strong>
            </template>
            <template v-slot:body.append>
              <tr class="report-table-total">
                <td>Total</td>
                <td></td>
                <td class="text-right">{{ totals.invoiceCount }}</td>
                <td class="text-right">{{ totals.parentCount }}</td>
                <td class="text-right">{{ totals.chargeCount }}</td>
                <td class="text-right">{{ totals.invoiced | currency }}</td>
                <td class="text-right">{{ totals.collected | currency }}</td>
                <td class="text-right">{{ totals.merchantCollected | currency }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </v-container>
  </div>
</template>

<script>
import Highcharts from "highcharts";
import ReportViewBar from "@/components/ReportViewBar";
import paymentService from "@/modules/Payment/service";
import { downloadFeeTypeCollectionPdf } from "./feeTypeCollectionReportPdf";
import "@/modules/ReportView/report.css";
import { mapGetters } from "vuex";

export default {
  components: { ReportViewBar },
  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      error: null,
      isDownloadingPdf: false,
      rows: [],
      headers: [
        { text: "FEE TYPE", value: "feeTypeName", align: "left" },
        { text: "DEPARTMENT", value: "departmentName", align: "left" },
        { text: "INVOICES", value: "invoiceCount", align: "right" },
        { text: "PARENTS", value: "parentCount", align: "right" },
        { text: "CHARGES", value: "chargeCount", align: "right" },
        { text: "AMOUNT INVOICED", value: "invoicedAmount", align: "right" },
        { text: "AMOUNT COLLECTED", value: "collectedAmount", align: "right" },
        { text: "MERCHANT COLLECTED", value: "merchantCollectedAmount", align: "right" },
      ],
    };
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    formattedPeriod() {
      if (!this.start || !this.end) return "Selected period";
      return `${this.$moment(this.start).format("MMM D, YYYY")} – ${this.$moment(this.end).format("MMM D, YYYY")}`;
    },
    totals() {
      const totals = this.rows.reduce((result, row) => ({
        invoiceCount: result.invoiceCount + row.invoiceCount,
        parentCount: result.parentCount + row.parentCount,
        chargeCount: result.chargeCount + row.chargeCount,
        invoiced: result.invoiced + row.invoicedAmount,
        collected: result.collected + row.collectedAmount,
        merchantCollected: result.merchantCollected + row.merchantCollectedAmount,
      }), { invoiceCount: 0, parentCount: 0, chargeCount: 0, invoiced: 0, collected: 0, merchantCollected: 0 });
      totals.collectionRate = totals.invoiced > 0
        ? ((totals.collected / totals.invoiced) * 100).toFixed(1)
        : "0.0";
      return totals;
    },
  },
  created() {
    this.start = this.$route.query.start;
    this.end = this.$route.query.end;
    this.loadReport();
  },
  methods: {
    async downloadPdf() {
      this.isDownloadingPdf = true;
      try {
        await downloadFeeTypeCollectionPdf({
          business: this.currentBusiness,
          rows: this.rows,
          totals: this.totals,
          start: this.start,
          end: this.end,
        });
      } catch (error) {
        console.error("Unable to create fee type collection PDF", error);
        this.$swal({ icon: "error", title: "PDF download failed", text: "Please try again." });
      } finally {
        this.isDownloadingPdf = false;
      }
    },
    number(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    normalizeRows(payload) {
      const source = Array.isArray(payload) ? payload : payload.rows || payload.data || [];
      return source.map((row, index) => ({
        feeTypeId: row.feeTypeId || row.productId || index,
        feeTypeName: row.feeTypeName || row.productName || row.name || "Unassigned",
        departmentId: row.departmentId || null,
        departmentName: row.departmentName || "Department not assigned",
        displayName: row.displayName || `${row.feeTypeName || row.productName || row.name || "Unassigned"} (${row.departmentName || "Department not assigned"})`,
        invoiceCount: this.number(row.invoiceCount),
        parentCount: this.number(row.parentCount),
        chargeCount: this.number(row.chargeCount),
        invoicedAmount: this.number(row.invoicedAmount),
        collectedAmount: this.number(row.collectedAmount),
        merchantCollectedAmount: this.number(row.merchantCollectedAmount),
      })).sort((left, right) =>
        left.departmentName.localeCompare(right.departmentName)
        || left.feeTypeName.localeCompare(right.feeTypeName));
    },
    renderChart() {
      if (!this.$refs.chartContainer || !this.rows.length) return;
      const sorted = [...this.rows].sort((left, right) =>
        left.departmentName.localeCompare(right.departmentName)
        || left.feeTypeName.localeCompare(right.feeTypeName));
      Highcharts.chart(this.$refs.chartContainer, {
        credits: { enabled: false },
        chart: { type: "bar", backgroundColor: "transparent", height: Math.max(320, sorted.length * 48) },
        title: { text: null },
        xAxis: { categories: sorted.map(row => row.displayName), title: { text: null } },
        yAxis: { min: 0, title: { text: "Amount" } },
        legend: { align: "center", verticalAlign: "bottom" },
        tooltip: { shared: true, valuePrefix: "$", valueDecimals: 2 },
        plotOptions: { series: { borderRadius: 3, groupPadding: 0.12 } },
        colors: [this.$vuetify.theme.currentTheme.primary],
        series: [{ name: "Amount collected", data: sorted.map(row => row.collectedAmount) }],
      });
    },
    async loadReport() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await paymentService.feeTypeCollectionSummary({ start: this.start, end: this.end });
        const payload = response && response.data != null ? response.data : response;
        this.rows = this.normalizeRows(payload || []);
        this.isLoading = false;
        await this.$nextTick();
        this.renderChart();
      } catch (error) {
        this.error = "We couldn't load the fee type collection report.";
        console.error("Error loading fee type collection report", error);
      } finally {
        this.isLoading = false;
        this.$Progress.finish();
      }
    },
  },
};
</script>

<style scoped>
.fee-type-collection-report__chart {
  min-height: 320px;
  padding: 8px 12px 16px;
}
.report-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; justify-content: flex-end; }
.merchant-amount { color: #285f8f; }
@media (min-width: 1264px) {
  .report-kpi-col { flex: 0 0 20%; max-width: 20%; }
}
@media (max-width: 600px) {
  .report-actions { align-items: stretch; flex-direction: column; width: 100%; }
}
</style>
